export const MANAGER_EMAIL_PREVIEW_SAFETY_NOTE =
  "Preview only — review subject and body before sending. This does not email managers until you click Send Email.";

export function formatMissingStoreRecipientsNote(storeNames) {
  if (!Array.isArray(storeNames) || storeNames.length === 0) {
    return null;
  }
  const list = storeNames.join(", ");
  if (storeNames.length === 1) {
    return `Missing store manager email for ${list} — add contacts in Settings before sending.`;
  }
  return `Missing store manager emails for ${list} — add contacts in Settings before sending.`;
}

export const INVENTORY_CLEANUP_TASK_TABS = {
  ACTIVE: "active",
  AWAITING: "awaiting",
  PERSISTING: "persisting",
  COMPLETED: "completed",
};

const CLEANUP_TAB_LABELS = {
  [INVENTORY_CLEANUP_TASK_TABS.ACTIVE]: "Active",
  [INVENTORY_CLEANUP_TASK_TABS.AWAITING]: "Awaiting store completion",
  [INVENTORY_CLEANUP_TASK_TABS.PERSISTING]: "Persisting",
  [INVENTORY_CLEANUP_TASK_TABS.COMPLETED]: "Completed",
};

export function normalizeCleanupTabFilter(value) {
  if (!value || typeof value !== "string") {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (!normalized || normalized === INVENTORY_CLEANUP_TASK_TABS.ACTIVE) {
    return null;
  }
  return Object.values(INVENTORY_CLEANUP_TASK_TABS).includes(normalized) ? normalized : null;
}

export function cleanupTabFilterLabel(tab) {
  if (!tab) {
    return null;
  }
  return CLEANUP_TAB_LABELS[tab] || null;
}

/** URL filter for awaiting-tab persistence states (scorecard deep links). */
export const CLEANUP_ESCALATION_FILTERS = {
  INCONCLUSIVE: "inconclusive",
  PENDING_CHECK: "pending_check",
  RESOLVED_CANDIDATE: "resolved_candidate",
};

const CLEANUP_ESCALATION_LABELS = {
  [CLEANUP_ESCALATION_FILTERS.INCONCLUSIVE]: "Inconclusive persistence",
  [CLEANUP_ESCALATION_FILTERS.PENDING_CHECK]: "Pending check",
  [CLEANUP_ESCALATION_FILTERS.RESOLVED_CANDIDATE]: "Resolved candidate",
};

export function normalizeCleanupEscalationFilter(value) {
  if (!value || typeof value !== "string") {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  return Object.values(CLEANUP_ESCALATION_FILTERS).includes(normalized) ? normalized : null;
}

export function cleanupEscalationFilterLabel(filter) {
  if (!filter) {
    return null;
  }
  return CLEANUP_ESCALATION_LABELS[filter] || null;
}

export function taskMatchesCleanupEscalationFilter(task, filter) {
  if (!filter) {
    return true;
  }
  if (task?.status !== "awaiting_store_completion" || isPersistingInventoryCleanupTask(task)) {
    return false;
  }
  const escalationStatus = task?.escalation_status;
  const checkResult = task?.last_inventory_check_result;
  if (filter === CLEANUP_ESCALATION_FILTERS.INCONCLUSIVE) {
    return escalationStatus === "inconclusive" || checkResult === "inconclusive";
  }
  if (filter === CLEANUP_ESCALATION_FILTERS.PENDING_CHECK) {
    return escalationStatus === "pending_check";
  }
  if (filter === CLEANUP_ESCALATION_FILTERS.RESOLVED_CANDIDATE) {
    return escalationStatus === "resolved_candidate";
  }
  return true;
}

const ACTIVE_STATUSES = new Set(["draft", "reviewed", "email_ready"]);
const COMPLETED_STATUSES = new Set(["verified_removed", "data_error", "no_action_needed"]);

export function isPersistingInventoryCleanupTask(task) {
  if (
    task?.status !== "awaiting_store_completion" ||
    task?.email_status !== "sent"
  ) {
    return false;
  }
  if (task.last_inventory_check_result === "persisting") {
    return true;
  }
  return task.escalation_status === "persisting";
}

export function getInventoryCleanupEscalationEmptyMessage(escalationFilter) {
  const label = cleanupEscalationFilterLabel(normalizeCleanupEscalationFilter(escalationFilter));
  if (!label) {
    return null;
  }
  return `No cleanup tasks with ${label.toLowerCase()} for the current filters. Clear the persistence filter or pick another tab.`;
}

export function getInventoryCleanupTaskTabEmptyMessage(tab, escalationFilter = null) {
  const escalationMessage = getInventoryCleanupEscalationEmptyMessage(escalationFilter);
  if (escalationMessage) {
    return escalationMessage;
  }
  if (tab === INVENTORY_CLEANUP_TASK_TABS.PERSISTING) {
    return "No persisting cleanup tasks. Run a persistence check on awaiting tasks to confirm inventory is still on hand.";
  }
  if (tab === INVENTORY_CLEANUP_TASK_TABS.AWAITING) {
    return "No tasks awaiting store completion. Send a manager email from an active task to start follow-up.";
  }
  if (tab === INVENTORY_CLEANUP_TASK_TABS.COMPLETED) {
    return "No completed cleanup tasks yet.";
  }
  return "No active cleanup tasks. Create one from an expired inventory card in Command Center.";
}

// Escalation badge labels for awaiting_store_completion tasks.
const AWAITING_ESCALATION_STATUS_LABELS = {
  persisting: "Persisting",
  resolved_candidate: "Resolved Candidate",
  inconclusive: "Inconclusive",
  max_escalation_reached: "Max Escalation Reached",
};

function getAwaitingStoreCompletionStatusLabel(task) {
  if (task?.email_status !== "sent") {
    return "Awaiting Store Completion";
  }
  const escalationStatus = task?.escalation_status;
  if (escalationStatus && AWAITING_ESCALATION_STATUS_LABELS[escalationStatus]) {
    return AWAITING_ESCALATION_STATUS_LABELS[escalationStatus];
  }
  return "Email Sent";
}

export function getInventoryCleanupTaskStatusLabel(task) {
  const status = task?.status;
  if (!status) {
    return "Draft";
  }
  if (status === "awaiting_store_completion") {
    return getAwaitingStoreCompletionStatusLabel(task);
  }
  if (status === "draft") {
    return "Draft";
  }
  if (status === "reviewed" || status === "email_ready") {
    return "Reviewed";
  }
  if (status === "verified_removed") {
    return "Verified Removed";
  }
  if (status === "data_error") {
    return "Data Error";
  }
  if (status === "no_action_needed") {
    return "No Action Needed";
  }
  return String(status)
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function filterInventoryCleanupTasksByTab(tasks, tab) {
  if (!Array.isArray(tasks)) {
    return [];
  }
  if (tab === INVENTORY_CLEANUP_TASK_TABS.AWAITING) {
    return tasks.filter(
      (task) => task.status === "awaiting_store_completion" && !isPersistingInventoryCleanupTask(task)
    );
  }
  if (tab === INVENTORY_CLEANUP_TASK_TABS.PERSISTING) {
    return tasks.filter((task) => isPersistingInventoryCleanupTask(task));
  }
  if (tab === INVENTORY_CLEANUP_TASK_TABS.COMPLETED) {
    return tasks.filter((task) => COMPLETED_STATUSES.has(task.status));
  }
  return tasks.filter((task) => ACTIVE_STATUSES.has(task.status));
}

export function filterInventoryCleanupTasksForView(tasks, tab, escalationFilter) {
  const byTab = filterInventoryCleanupTasksByTab(tasks, tab);
  const normalizedEscalation = normalizeCleanupEscalationFilter(escalationFilter);
  if (!normalizedEscalation) {
    return byTab;
  }
  return byTab.filter((task) => taskMatchesCleanupEscalationFilter(task, normalizedEscalation));
}

export function canSendInventoryCleanupEmail(task, preview) {
  if (!task || task.status === "awaiting_store_completion") {
    return false;
  }
  if (COMPLETED_STATUSES.has(task.status)) {
    return false;
  }
  const recipients = preview?.recipient_emails || [];
  const missingRecipients = preview?.missing_recipients || [];
  const subject = preview?.subject || "";
  const body = preview?.body || "";
  return (
    recipients.length > 0 &&
    missingRecipients.length === 0 &&
    Boolean(subject.trim()) &&
    Boolean(body.trim())
  );
}

export function getInventoryCleanupSendDisabledReason(task, preview, { busy = false } = {}) {
  if (busy) {
    return "Another action is in progress.";
  }
  if (canSendInventoryCleanupEmail(task, preview)) {
    return null;
  }
  if (!task) {
    return "Task not found.";
  }
  if (task.status === "awaiting_store_completion") {
    return "Email was already sent. Task is awaiting store completion.";
  }
  if (COMPLETED_STATUSES.has(task.status)) {
    return "This task is already completed.";
  }
  if (!preview) {
    return "Generate an email preview before sending.";
  }
  const recipients = preview.recipient_emails || [];
  const missingRecipients = preview.missing_recipients || [];
  if (missingRecipients.length > 0) {
    const note = formatMissingStoreRecipientsNote(missingRecipients);
    return note || "Missing store manager emails — add contacts in Settings before sending.";
  }
  if (recipients.length === 0) {
    return "No store manager recipients are configured for this task.";
  }
  if (!String(preview.subject || "").trim()) {
    return "Email preview is missing a subject line.";
  }
  if (!String(preview.body || "").trim()) {
    return "Email preview is missing a body.";
  }
  return "Send is not available for this task.";
}

export function formatEmailRecipientList(recipients) {
  if (!Array.isArray(recipients) || recipients.length === 0) {
    return "";
  }
  return recipients.filter(Boolean).join(", ");
}

export function isInventoryCleanupTaskSendable(task) {
  return ACTIVE_STATUSES.has(task?.status);
}

export function isInventoryCleanupTaskAwaiting(task) {
  return task?.status === "awaiting_store_completion" && task?.email_status === "sent";
}

export function isInventoryCleanupTaskCompletable(task) {
  if (!task || COMPLETED_STATUSES.has(task.status)) {
    return false;
  }
  return ["draft", "reviewed", "email_ready", "awaiting_store_completion"].includes(task.status);
}
